import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { ServiceType } from '@shared/providers/theme/types';
import { getPathAfterServiceSwitch } from '@/constants/routes';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';

/**
 * Service Type Selector Component
 * Used in both Sidebar (bottom fixed) and Design Template (demo section)
 *
 * Variants:
 * - sidebar: Full colored buttons with theme config styling (for fixed sidebar position)
 * - inline: Same button styling but without the fixed wrapper (for design template demo)
 */
interface ServiceTypeSelectorProps {
  currentType: ServiceType;
  onTypeChange: (type: ServiceType) => void;
  canSwitch: boolean;
  a2Colors: { primary: string; primaryDark: string; sidebarBg: string; sidebarBgInactive: string };
  r2Colors: { primary: string; primaryDark: string; sidebarBg: string; sidebarBgInactive: string };
  variant?: 'sidebar' | 'inline';
  themeConfig?: {
    serviceSelector: {
      backgroundColor: string;
      buttonActiveOpacity: string;
      buttonInactiveOpacity: string;
    };
  };
}

const ServiceTypeSelector: React.FC<ServiceTypeSelectorProps> = ({
  currentType,
  onTypeChange,
  canSwitch,
  a2Colors,
  r2Colors,
  variant = 'inline',
  themeConfig,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleServiceSwitch = (newType: 'A2' | 'R2') => {
    onTypeChange(newType);

    // Determine where to navigate after service switch
    // Cast currentType to 'A2' | 'R2' since it could be 'adminTheme'
    if (currentType === SERVICE_TYPE_A2 || currentType === SERVICE_TYPE_R2) {
      const newPath = getPathAfterServiceSwitch(location.pathname, currentType);
      if (newPath !== location.pathname) {
        navigate(newPath);
      }
    }
  };

  if (!canSwitch) {
    return (
      <div className="text-sm italic text-gray-500">
        (This clinic is restricted to {currentType} only)
      </div>
    );
  }

  // Reusable button group for both variants
  const buttonGroup = (
    <div className="flex gap-1">
      <button
        onClick={() => handleServiceSwitch(SERVICE_TYPE_A2)}
        style={{
          backgroundColor:
            currentType === SERVICE_TYPE_A2 ? `${a2Colors.sidebarBg}` : `${a2Colors.sidebarBgInactive}`,
          color: '#ffffff',
        }}
        className="flex-1 px-4 py-2 text-sm font-medium leading-tight text-center transition-colors rounded"
      >
        <span className="block whitespace-nowrap">Recepro A</span>
        <span className="block text-xs whitespace-nowrap">(はり・きゅう)</span>
      </button>
      <button
        onClick={() => handleServiceSwitch(SERVICE_TYPE_R2)}
        style={{
          backgroundColor:
            currentType === SERVICE_TYPE_R2 ? `${r2Colors.sidebarBg}` : `${r2Colors.sidebarBgInactive}`,
          color: '#ffffff',
        }}
        className="flex-1 px-4 py-2 text-sm font-medium leading-tight text-center transition-colors rounded"
      >
        <span className="block whitespace-nowrap">Recepro R</span>
        <span className="block text-xs whitespace-nowrap">(マッサージ)</span>
      </button>
    </div>
  );

  // Sidebar variant: with fixed wrapper and shadow
  if (variant === 'sidebar' && themeConfig) {
    const bgColor = currentType === SERVICE_TYPE_A2 ? a2Colors.primaryDark : r2Colors.primaryDark;
    return (
      <div
        className="flex-shrink-0 p-2 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.3)]"
        style={{ backgroundColor: bgColor }}
      >
        {buttonGroup}
      </div>
    );
  }

  // Inline variant: just the buttons (for design template demo)
  return buttonGroup;
};

export default ServiceTypeSelector;
